// PropertySummaryTab.js
import React, { useEffect, useState } from 'react';
import { Table } from 'antd';
import { preparePropertySummaryData, formatCurrency } from '../../../utils/dataUtils';

/**
 * Property Summary tab component showing property-based metrics
 * @param {Object} props - Component props
 * @param {Array} props.reportData - Processed report data
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.excludeCleaningServices - Whether cleaning services are excluded
 * @param {boolean} props.showPricesWithoutVAT - Whether to show prices without VAT
 */
const PropertySummaryTab = ({ 
  reportData, 
  loading, 
  excludeCleaningServices,
  showPricesWithoutVAT,
}) => {
  const [processedData, setProcessedData] = useState([]);
  
  // Process report data based on filters
  useEffect(() => {
    // Generate property summary with filtered data
    const summarizedData = preparePropertySummaryData(reportData, { 
      excludeCleaningServices, 
      showPricesWithoutVAT 
    });
    setProcessedData(summarizedData);
  }, [reportData, excludeCleaningServices, showPricesWithoutVAT]);

  // Property summary table columns
  const getPropertySummaryColumns = () => {
    const baseColumns = [
      {
        title: 'Property',
        dataIndex: 'propertyTitle',
        key: 'propertyTitle',
        sorter: (a, b) => a.propertyTitle.localeCompare(b.propertyTitle),
        ellipsis: true,
        width: 200
      },
      {
        title: 'Address',
        dataIndex: 'propertyAddress',
        key: 'propertyAddress',
        ellipsis: true,
        width: 250
      },
      {
        title: 'Total Orders',
        dataIndex: 'totalOrders',
        key: 'totalOrders',
        sorter: (a, b) => a.totalOrders - b.totalOrders,
        width: 120
      },
      {
        title: 'Successful',
        dataIndex: 'successfulOrders',
        key: 'successfulOrders',
        render: (value) => <span className="text-green-600 font-medium">{value}</span>,
        sorter: (a, b) => a.successfulOrders - b.successfulOrders,
        width: 120
      },
      {
        title: 'Failed',
        dataIndex: 'failedOrders',
        key: 'failedOrders',
        render: (value) => <span className="text-red-600 font-medium">{value}</span>,
        sorter: (a, b) => a.failedOrders - b.failedOrders,
        width: 120
      },
      {
        title: 'Pending',
        dataIndex: 'pendingOrders',
        key: 'pendingOrders',
        render: (value) => <span className="text-blue-600 font-medium">{value}</span>,
        sorter: (a, b) => a.pendingOrders - b.pendingOrders,
        width: 120
      },
      {
        title: showPricesWithoutVAT ? 'Total (excl. VAT)' : 'Total',
        dataIndex: 'totalRevenue',
        key: 'totalRevenue',
        render: (value) => formatCurrency(value),
        sorter: (a, b) => a.totalRevenue - b.totalRevenue,
        width: 150,
        align: 'right'
      }
    ];
    
    // Add VAT column if showing prices without VAT
    if (showPricesWithoutVAT) {
      baseColumns.push({
        title: 'VAT Amount',
        dataIndex: 'totalVAT',
        key: 'totalVAT',
        render: (value) => formatCurrency(value),
        width: 120,
        align: 'right'
      });
      
      // Add Total Including VAT column
      baseColumns.push({
        title: 'Total (incl. VAT)',
        key: 'totalWithVAT',
        render: (_, record) => formatCurrency(record.totalRevenue + record.totalVAT),
        width: 150,
        align: 'right'
      });
    }
    
    return baseColumns;
  };

  // Get dynamic columns
  const propertySummaryColumns = getPropertySummaryColumns();

  // Calculate summary totals
  const calculateSummary = () => {
    const data = processedData;
    return {
      totalProperties: data.length,
      totalOrders: data.reduce((sum, property) => sum + property.totalOrders, 0),
      totalSuccessful: data.reduce((sum, property) => sum + property.successfulOrders, 0),
      totalFailed: data.reduce((sum, property) => sum + property.failedOrders, 0),
      totalPending: data.reduce((sum, property) => sum + property.pendingOrders, 0),
      totalRevenue: data.reduce((sum, property) => sum + property.totalRevenue, 0),
      totalVAT: data.reduce((sum, property) => sum + (property.totalVAT || 0), 0)
    };
  };

  const summary = calculateSummary();

  // Table summary component with dynamically sized columns
  const tableSummary = () => {
    // Calculate standard column count and adjust if VAT columns are present
    const baseColSpan = 2;
    
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={baseColSpan} align="right">
            <strong>Total ({summary.totalProperties} properties):</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2}>
            <strong>{summary.totalOrders}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3}>
            <strong className="text-green-600">{summary.totalSuccessful}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4}>
            <strong className="text-red-600">{summary.totalFailed}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5}>
            <strong className="text-blue-600">{summary.totalPending}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={6} align="right">
            <strong>{formatCurrency(summary.totalRevenue)}</strong>
          </Table.Summary.Cell>
          
          {/* VAT columns in summary if needed */}
          {showPricesWithoutVAT && (
            <>
              <Table.Summary.Cell index={7} align="right">
                <strong>{formatCurrency(summary.totalVAT)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} align="right">
                <strong>{formatCurrency(summary.totalRevenue + summary.totalVAT)}</strong>
              </Table.Summary.Cell>
            </>
          )}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <Table
      columns={propertySummaryColumns}
      dataSource={processedData}
      rowKey="propertyId"
      loading={loading}
      scroll={{ x: showPricesWithoutVAT ? 1400 : 1100 }}
      pagination={{
        defaultPageSize: 25,
        pageSizeOptions: ['25', '50', '100', '200'],
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} properties`
      }}
      summary={tableSummary}
    />
  );
};

export default PropertySummaryTab;
